import React from 'react';
import moment from 'moment';
import { History } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  AgnesHistoryTimeline,
  AgnesPageHeader,
  AgnesSkeleton,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
  type AgnesHistoryTimelineItem,
} from '@agnes/ui';
import { ExportButton } from '@/components/ExportButton';
import { SearchInput } from '@/components/SearchInput';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';
import { comQaNasCelulas } from '@/lib/dataQa';
import { countFiltros } from '@/lib/filtros';
import { useCboStore } from '@/stores/useCboStore';
import { confirm } from '@/stores/useConfirm';
import { usePodeEditar } from '@/hooks/usePodeEditar';
import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';
import FormCboAnalise from './FormCboAnalise';
import CboFilter, { type CboFilterField } from '../CboFilter';
import {
  FILTROS_CBO_EVENTOS_VAZIOS,
  useCboMovimentacoesStore,
  CBO_EVENTOS_SORT_ALIASES,
  type CboEventosFiltros,
  type CboMovimentacao,
} from '@/stores/useCboMovimentacoesStore';

const dataQa = 'cbo-analise';

const CBO_MOVIMENTACOES_COLUMNS = [
  {
    key: 'dataOcorrencia',
    header: 'Ocorrência do Evento',
    width: 180,
    sortable: Boolean(CBO_EVENTOS_SORT_ALIASES.dataOcorrencia),
    render: (row: CboMovimentacao) => formatarData(row.dataOcorrencia, 'DD/MM/YYYY'),
  },
  {
    key: 'registerDate',
    header: 'Recepção no eSocial',
    width: 200,
    sortable: Boolean(CBO_EVENTOS_SORT_ALIASES.registerDate),
    render: (row: CboMovimentacao) => formatarData(row.registerDate, 'DD/MM/YYYY HH:mm:ss'),
  },
  {
    key: 'idDocument',
    header: 'Identificação do Evento',
    width: 300,
    sortable: Boolean(CBO_EVENTOS_SORT_ALIASES.idDocument),
    render: (row: CboMovimentacao) => valorOuVazio(row.idDocument),
  },
  {
    key: 'submissionType',
    header: 'Declaração',
    width: 140,
    sortable: Boolean(CBO_EVENTOS_SORT_ALIASES.submissionType),
    render: (row: CboMovimentacao) => rotuloDeclaracao(row.submissionType),
  },
  {
    key: 'receiptNumber',
    header: 'Número do Recibo',
    width: 220,
    sortable: Boolean(CBO_EVENTOS_SORT_ALIASES.receiptNumber),
    render: (row: CboMovimentacao) => valorOuVazio(row.receiptNumber),
  },
  {
    key: 'receiptNumberRectification',
    header: 'Número do Recibo Retificado',
    width: 240,
    sortable: Boolean(CBO_EVENTOS_SORT_ALIASES.receiptNumberRectification),
    render: (row: CboMovimentacao) =>
      row.submissionType === 'ORIGINAL'
        ? 'Não aplicável'
        : valorOuVazio(row.receiptNumberRectification),
  },
  {
    key: 'version',
    header: 'Versão eSocial',
    width: 140,
    sortable: Boolean(CBO_EVENTOS_SORT_ALIASES.version),
    render: (row: CboMovimentacao) => valorOuVazio(row.version),
  },
  {
    key: 'esocialEventDescription',
    header: 'Evento',
    width: 320,
    sortable: Boolean(CBO_EVENTOS_SORT_ALIASES.esocialEventDescription),
    render: (row: CboMovimentacao) => {
      const evento = [row.esocialEventCode, row.esocialEventDescription]
        .filter(Boolean)
        .join(' - ');
      return `${evento || '—'}${row.excluded ? ' (Excluído)' : ''}`;
    },
  },
  {
    key: 'importDate',
    header: 'Importação em Agnes',
    width: 200,
    sortable: Boolean(CBO_EVENTOS_SORT_ALIASES.importDate),
    render: (row: CboMovimentacao) => formatarData(row.importDate, 'DD/MM/YYYY [às] HH[h]mm'),
  },
];

type LinhaHistorico = { [k: string]: any };

const Vazio = () => <span className="text-agnes-text-muted">—</span>;

function formatarData(valor: string | null | undefined, formato: string) {
  if (!valor) return '—';
  const data = moment(valor, moment.ISO_8601);
  return data.isValid() ? data.format(formato) : valor;
}

function rotuloDeclaracao(valor: string | null | undefined) {
  if (valor === 'ORIGINAL') return 'Original';
  if (valor === 'RETIFICACAO') return 'Retificação';
  return valor || '—';
}

function valorOuVazio(valor: string | null | undefined) {
  return valor == null || valor === '' ? '—' : valor;
}

function normalizarDataRange(valor: string[]) {
  return valor.length === 2 && valor[0] && valor[1] ? valor : [];
}

const EMPTY_CBO_EVENT_FILTER_FORM: Record<string, string> = {
  importDateStart: '',
  importDateEnd: '',
  dataOcorrenciaStart: '',
  dataOcorrenciaEnd: '',
  registerDateStart: '',
  registerDateEnd: '',
  submissionType: '',
  version: '',
  esocialEventCode: '',
};

function AbaMovimentacoes({ trabalhadorId }: { trabalhadorId: number }) {
  const movimentacoes = useCboMovimentacoesStore();
  const inicializar = useCboMovimentacoesStore((state) => state.inicializar);
  const [busca, setBusca] = React.useState('');

  const camposFiltro = React.useMemo<CboFilterField[]>(
    () => [
      {
        key: 'importDateStart',
        endKey: 'importDateEnd',
        placeholder: 'Data de importação em Agnes',
        type: 'dateRange',
      },
      {
        key: 'dataOcorrenciaStart',
        endKey: 'dataOcorrenciaEnd',
        placeholder: 'Data de Ocorrência',
        type: 'dateRange',
      },
      {
        key: 'registerDateStart',
        endKey: 'registerDateEnd',
        placeholder: 'Data de Registro',
        type: 'dateRange',
      },
      {
        key: 'submissionType',
        placeholder: 'Declaração',
        options: [
          { value: 'ORIGINAL', label: 'Original' },
          { value: 'RETIFICACAO', label: 'Retificação' },
        ],
      },
      {
        key: 'version',
        placeholder: 'Versão do eSocial',
        options: movimentacoes.opcoes.versions.map((version) => ({
          value: version,
          label: version,
        })),
      },
      {
        key: 'esocialEventCode',
        placeholder: 'Evento',
        options: movimentacoes.opcoes.events.map((event) => ({
          value: event.code,
          label: [event.code, event.description].filter(Boolean).join(' - '),
        })),
      },
    ],
    [movimentacoes.opcoes],
  );

  React.useEffect(() => {
    setBusca('');
    void inicializar(trabalhadorId);
  }, [trabalhadorId, inicializar]);

  const montarFiltrosEventos = (form: Record<string, string>): CboEventosFiltros => ({
    ...FILTROS_CBO_EVENTOS_VAZIOS,
    importDate: normalizarDataRange([form.importDateStart ?? '', form.importDateEnd ?? '']),
    registerDate: normalizarDataRange([form.registerDateStart ?? '', form.registerDateEnd ?? '']),
    dataOcorrencia: normalizarDataRange([
      form.dataOcorrenciaStart ?? '',
      form.dataOcorrenciaEnd ?? '',
    ]),
    submissionType: form.submissionType ? [form.submissionType] : [],
    version: form.version ? [form.version] : [],
    esocialEventCode: form.esocialEventCode ? [form.esocialEventCode] : [],
    search: movimentacoes.filtros.search,
  });

  const buscarEventos = (value: string) => {
    void movimentacoes.aplicarFiltros(trabalhadorId, {
      ...movimentacoes.filtros,
      search: value.trim(),
    });
  };

  return (
    <div data-qa={`${dataQa}-movimentacoes`}>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="min-w-[320px] flex-1">
          <SearchInput
            dataQa={`${dataQa}-movimentacoes-busca`}
            value={busca}
            onValueChange={setBusca}
            onSearch={buscarEventos}
            placeholder="Pesquisar por ID, evento ou número do recibo"
          />
        </div>
        <CboFilter<CboEventosFiltros>
          key={trabalhadorId}
          fields={camposFiltro}
          initialValues={EMPTY_CBO_EVENT_FILTER_FORM}
          toFilter={montarFiltrosEventos}
          dataQaPrefix={`${dataQa}-movimentacoes-filtro`}
          count={countFiltros(movimentacoes.filtros, { ignore: ['rootCnpjList', 'search'] })}
          onApply={(filtros) => void movimentacoes.aplicarFiltros(trabalhadorId, filtros)}
        />
        <ExportButton
          dataQa={`${dataQa}-movimentacoes-exportar`}
          variant="outlined"
          disabled={movimentacoes.exportando}
          onClick={() => void movimentacoes.exportar(trabalhadorId)}
        >
          Exportar
        </ExportButton>
      </div>
      <TableWrapper>
        <AgnesDataTable<CboMovimentacao>
          dataQa={`${dataQa}-movimentacoes-tabela`}
          columns={comQaNasCelulas(
            `${dataQa}-movimentacoes-tabela`,
            CBO_MOVIMENTACOES_COLUMNS,
            (row) => String(row.eventoId),
          )}
          data={movimentacoes.eventos}
          loading={movimentacoes.carregando}
          rowKey={(row) => String(row.eventoId)}
          sort={{
            sortBy: movimentacoes.sortBy,
            sortOrder: movimentacoes.sortOrder,
            onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') =>
              void movimentacoes.setSort(trabalhadorId, sortBy, sortOrder),
          }}
          pagination={{
            page: movimentacoes.page,
            pageSize: 50,
            total: movimentacoes.total,
            serverSide: true,
            onPageChange: (page: number) => void movimentacoes.setPage(trabalhadorId, page),
            onPageSizeChange: () => {},
          }}
        />
      </TableWrapper>
    </div>
  );
}

const fmtQuando = (v: string | null | undefined): string | null => {
  if (!v) return null;
  const m = moment(v, moment.ISO_8601);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : v;
};

/**
 * Tela de análise do CBO com edição, histórico e eventos eSocial do trabalhador.
 * O histórico é exibido pelo AgnesHistoryTimeline e a análise pode ser editada por perfis autorizados.
 */
export default function PageTrabalhadorCboAnalise() {
  const { id } = useParams();
  const navigate = useNavigate();
  const trabalhadorId = Number(id);

  const historico = useCboStore((s) => s.historico);
  const carregarAnalise = useCboStore((s) => s.carregarAnalise);
  const carregarHistoricoAnalise = useCboStore((s) => s.carregarHistoricoAnalise);

  // Loading local da tela: cobre a análise + o histórico (que vem logo depois),
  // evitando o "flash" de estado vazio entre as duas chamadas. Regra do projeto:
  // skeleton no carregamento da tela.
  const [carregando, setCarregando] = React.useState(true);

  React.useEffect(() => {
    if (!Number.isFinite(trabalhadorId)) return;
    let ativo = true;
    setCarregando(true);
    carregarAnalise(trabalhadorId)
      .then(() => carregarHistoricoAnalise())
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [trabalhadorId, carregarAnalise, carregarHistoricoAnalise]);

  // Informações continua sendo a aba inicial para perfis com permissão de edição;
  // deep-links de Histórico e Movimentações também são preservados.
  const podeEditar = usePodeEditar(MENU_PERMISSIONS.CBO_EDITAR);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabInicial = searchParams.get('tab');
  const [tabEscolhida, setTab] = React.useState(
    tabInicial === 'historico' || tabInicial === 'movimentacoes' ? tabInicial : 'informacoes',
  );
  const tab = podeEditar
    ? tabEscolhida
    : tabEscolhida === 'movimentacoes'
      ? 'movimentacoes'
      : 'historico';
  // Movimentações só monta na 1ª visita (evita a chamada à API sem necessidade) e depois
  // fica montada, para busca, filtros, página e ordenação sobreviverem à troca de aba.
  const [movimentacoesVisitada, setMovimentacoesVisitada] = React.useState(
    tabEscolhida === 'movimentacoes',
  );

  // O formulário desmonta ao trocar de aba; sem o aviso, as edições se perderiam em silêncio.
  const [formSujo, setFormSujo] = React.useState(false);
  // O Radix dispara onValueChange no mousedown e de novo no foco; sem a trava o aviso abriria 2x.
  const confirmando = React.useRef(false);

  const mudarTab = async (v: string) => {
    if (tab === 'informacoes' && v !== 'informacoes' && formSujo) {
      if (confirmando.current) return;
      confirmando.current = true;
      try {
        const sair = await confirm({
          title: 'Alterações não salvas',
          message: 'Você tem alterações que não foram salvas e serão perdidas. Deseja sair?',
          okLabel: 'Sair',
          cancelLabel: 'Continuar editando',
        });
        if (!sair) return;
      } finally {
        confirmando.current = false;
      }
    }
    setFormSujo(false);
    setTab(v);
    if (v === 'movimentacoes') setMovimentacoesVisitada(true);
    const next = new URLSearchParams(searchParams);
    if (v === 'informacoes') next.delete('tab');
    else next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  // Cada item do histórico já é uma alteração pronta (campo De → Para), vinda do backend.
  const itens: AgnesHistoryTimelineItem[] = historico.map((r: LinhaHistorico) => ({
    key: String(r.id ?? `${r.changedField}-${r.updatedAt}`),
    content: (
      <p className="text-sm text-agnes-text">
        <span className="font-semibold">
          {r.changedFieldDescriptiveName ?? r.changedField ?? 'Alteração'}:
        </span>{' '}
        {r.fromValue || <Vazio />} → {r.toValue || <Vazio />}
      </p>
    ),
    detail: [r.updatedBy, fmtQuando(r.updatedAt)].filter(Boolean).join(' · '),
  }));

  return (
    <div data-qa={dataQa}>
      <AgnesPageHeader
        icon={<History />}
        title="Análise do CBO"
        subtitle="Compare a ocupação do cliente com as recomendações Agnes"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'CBO', href: '/esocial/trabalhador/cbo' },
          { label: 'Análise do CBO' },
        ]}
      />

      <AgnesTabs
        className="mt-2 w-full"
        value={tab}
        onValueChange={mudarTab}
        dataQa={`${dataQa}-abas`}
      >
        <AgnesTabsList dataQa={`${dataQa}-abas-lista`}>
          {podeEditar && (
            <AgnesTabsTrigger value="informacoes" data-qa={`${dataQa}-aba-informacoes`}>
              Informações
            </AgnesTabsTrigger>
          )}
          <AgnesTabsTrigger value="historico" data-qa={`${dataQa}-aba-historico`}>
            Histórico da análise
          </AgnesTabsTrigger>
          <AgnesTabsTrigger value="movimentacoes" data-qa={`${dataQa}-aba-movimentacoes`}>
            Movimentações
          </AgnesTabsTrigger>
        </AgnesTabsList>

        {podeEditar && (
          <AgnesTabsContent value="informacoes">
            <AgnesCard>
              <AgnesCardContent>
                <FormCboAnalise
                  onClose={() => navigate('/esocial/trabalhador/cbo')}
                  onDirtyChange={setFormSujo}
                />
              </AgnesCardContent>
            </AgnesCard>
          </AgnesTabsContent>
        )}

        <AgnesTabsContent value="historico">
          <AgnesCard>
            <AgnesCardContent>
              {carregando ? (
                <div data-qa={`${dataQa}-skeleton`} className="space-y-3 py-2">
                  <AgnesSkeleton className="h-5 w-1/3" />
                  <AgnesSkeleton className="h-5 w-2/3" />
                  <AgnesSkeleton className="h-5 w-1/2" />
                  <AgnesSkeleton className="h-5 w-3/5" />
                </div>
              ) : (
                <AgnesHistoryTimeline
                  items={itens}
                  dataQa="cbo-historico"
                  emptyTitle="Nenhuma alteração registrada"
                />
              )}
            </AgnesCardContent>
          </AgnesCard>
        </AgnesTabsContent>

        <AgnesTabsContent
          value="movimentacoes"
          dataQa={`${dataQa}-aba-movimentacoes-conteudo`}
          forceMount={movimentacoesVisitada || undefined}
          className="data-[state=inactive]:hidden"
        >
          {movimentacoesVisitada && (
            <AgnesCard>
              <AgnesCardContent>
                <AbaMovimentacoes trabalhadorId={trabalhadorId} />
              </AgnesCardContent>
            </AgnesCard>
          )}
        </AgnesTabsContent>
      </AgnesTabs>
    </div>
  );
}
